import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Input, Tag, Space, Drawer, Descriptions, Typography, message,
  Modal, Form, Select, InputNumber, Row, Col, Spin, Image, Timeline, Divider, Empty, Tooltip,
} from 'antd';
import { SearchOutlined, ThunderboltOutlined, EyeOutlined, ReloadOutlined, ClearOutlined, DeleteOutlined, FileSearchOutlined } from '@ant-design/icons';
import { kewellApi, appealApi, claimApi, customerApi, authApi, orderApi } from '../services/api';
import CreateOrderModal from '../components/CreateOrderModal';
import { fmtCN } from '../utils/time';
import { actionText } from '../utils/logLabels';
import { parseContent, logDeltaText, cleanLogRemark, renderLatestSegment } from '../utils/logRender';

const { Text, Paragraph } = Typography;

// 申诉原因选项
const APPEAL_REASON_OPTIONS = [
  { value: 'dimension_weight', label: '尺寸复量' },
  { value: 'weight_diff', label: '重量差异' },
  { value: 'extra_service', label: '额外服务' },
  { value: 'amount_diff', label: '收费金额差异' },
  { value: 'duplicate_charge', label: '重复收费' },
  { value: 'other', label: '其他' },
];

const FOLLOW_STRATEGY_OPTIONS = [
  { value: 'yes', label: '是' },
  { value: 'no', label: '否' },
];

// 索赔类型选项（美国 LTL/FTL 行业常见索赔类别）
const CLAIM_TYPE_OPTIONS = [
  { value: 'damage', label: '货物破损 Damage' },
  { value: 'shortage', label: '货物短少 Shortage' },
  { value: 'loss', label: '货物丢失 Loss' },
  { value: 'delay', label: '延迟交付 Delay' },
  { value: 'other', label: '其他 Other' },
];

const ESCALATE_HOURS_OPTIONS = [
  { value: '12h', label: '12小时' },
  { value: '1d', label: '1天' },
  { value: '3d', label: '3天' },
  { value: '5d', label: '5天' },
  { value: '10d', label: '10天' },
];

// 查询历史本地缓存（按登录用户隔离，最多保留100条）
const CACHE_KEY = () => {
  let user = '';
  try { user = JSON.parse(localStorage.getItem('user') || '{}')?.username || ''; } catch (e) {}
  return `kewell_order_cache_${user}`;
};
const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY()) || '[]'); } catch (e) { return []; }
};
const saveCache = (list) => {
  try { localStorage.setItem(CACHE_KEY(), JSON.stringify(list.slice(0, 100))); } catch (e) {}
};

// 科维斯订单状态 → Tag 颜色
const STATUS_COLOR = {
  1: 'default', 2: 'red', 3: 'orange', 4: 'blue', 5: 'green',
  6: 'default', 7: 'red', 8: 'gold',
};

// 调度工单展示映射
const ORDER_STATUS_MAP = { pending: ['调度待跟进', 'orange'], processing: ['调度处理中', 'blue'], awaiting_customer: ['待客服处理', 'purple'], completed: ['已完成', 'green'] };
const PRIORITY_MAP = { urgent: ['紧急重要', 'red'], high: ['一般重要', 'orange'], normal: ['普通', 'default'], low: ['普通', 'default'] };
// 日志动作/状态中文映射统一走 utils/logLabels

// 收发货信息 → 单行地址（含服务时间窗）
function fmtLocation(info) {
  if (!info) return '';
  const parts = [info.address, info.city, info.state_name, info.zip_code].filter(Boolean);
  let s = parts.join(', ').replace(/, (?=[A-Z]{2} \d)/, ', ');
  if (info.pickup_start_time || info.pickup_end_time) {
    s += `（服务时间 ${info.pickup_start_time || ''}-${info.pickup_end_time || ''}）`;
  }
  return s;
}

export default function KewellOrders() {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(loadCache);
  const [detail, setDetail] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [findingOrder, setFindingOrder] = useState(false);
  const [orderDrawer, setOrderDrawer] = useState(null); // {order, logs}
  // 单号 -> 是否已在调度系统生成工单（{exists, authorized, status}）
  const [existMap, setExistMap] = useState({});

  // 与「生成调度」预填/查看工单一致的单号口径：查询编码 > 平台订单号 > PRO号
  const orderKeyOf = (o) => o.track_number || o.order_no || o.pro_number || '';

  // 批量检查科维斯订单在调度系统内是否已生成工单（失败静默，不阻塞查单主流程）
  const mergeExistMap = async (list) => {
    const kws = Array.from(new Set((list || []).map(orderKeyOf).filter(Boolean)));
    if (!kws.length) return;
    try {
      const d = await orderApi.existsByNos(kws);
      setExistMap((prev) => {
        const next = { ...prev };
        (d.items || []).forEach((it) => { next[it.order_no] = it; });
        return next;
      });
    } catch (e) { /* 标记加载失败不影响主功能 */ }
  };

  // 账单申诉弹窗
  const [appealModalOpen, setAppealModalOpen] = useState(false);
  const [appealTarget, setAppealTarget] = useState(null);
  const [appealForm] = Form.useForm();
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  const [appealUploading, setAppealUploading] = useState(false);
  const [appealImages, setAppealImages] = useState([]);

  // 提交索赔弹窗
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [claimForm] = Form.useForm();
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimUploading, setClaimUploading] = useState(false);
  const [claimImages, setClaimImages] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [csUsers, setCsUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]); // 客服/调度/销售（带role）

  // 归属自动匹配角色优先级：客服 > 调度 > 销售
  const OWNER_ROLE_RANK = { customer_service: 0, dispatcher: 1, sales: 2 };
  const matchOwnerName = (customer) => {
    const ids = customer?.assigned_user_ids || [];
    const picked = ids
      .map((id) => assignableUsers.find((u) => u.id === id))
      .filter(Boolean)
      .sort((a, b) => (OWNER_ROLE_RANK[a.role] ?? 9) - (OWNER_ROLE_RANK[b.role] ?? 9))[0];
    return picked?.full_name || (customer?.assigned_names || [])[0] || '';
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [names, cs, au] = await Promise.all([
          customerApi.listNames(),
          authApi.csUsers(),
          authApi.assignableUsers(),
        ]);
        if (cancelled) return;
        setCustomerList(Array.isArray(names) ? names : []);
        setCsUsers(Array.isArray(cs) ? cs : []);
        setAssignableUsers(Array.isArray(au) ? au : []);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // 首次进入：对本地缓存的历史查询订单批量检查是否已生成工单
  useEffect(() => { mergeExistMap(loadCache()); /* eslint-disable-next-line */ }, []);

  const mergeOrder = (o) => {
    setOrders((prev) => {
      const key = o.order_no || o.track_number;
      const rest = prev.filter((x) => (x.order_no || x.track_number) !== key);
      const next = [{ ...o, _queried_at: new Date().toISOString() }, ...rest];
      saveCache(next);
      return next;
    });
  };

  const doQuery = async (kw, { silent } = {}) => {
    const data = await kewellApi.queryOrder(kw);
    const o = data.order;
    if (!o) {
      if (!silent) message.info('未查询到订单');
      return false;
    }
    mergeOrder(o);
    mergeExistMap([o]);
    if (!silent) message.success(`已查询到订单 ${o.order_no || o.track_number}`);
    return true;
  };

  const handleSearch = async () => {
    const kw = keyword.trim();
    if (!kw) {
      message.warning('请输入查询编码 / PRO号 / BOL号 / 订单编号');
      return;
    }
    setLoading(true);
    try {
      await doQuery(kw);
    } catch (e) {
      // 拦截器已统一提示（订单不存在/未配置凭据等）
    } finally {
      setLoading(false);
    }
  };

  // 刷新：输入框有词则重查该词；否则重新拉取列表内全部订单（实时数据，不落库）
  const handleRefresh = async () => {
    const kw = keyword.trim();
    const keys = kw
      ? [kw]
      : orders.map((o) => o.order_no || o.track_number).filter(Boolean);
    if (!keys.length) {
      message.info('暂无可刷新的订单，请先输入单号查询');
      return;
    }
    setLoading(true);
    let ok = 0;
    try {
      for (const k of keys) {
        try {
          if (await doQuery(k, { silent: true })) ok += 1;
        } catch (e) { /* 单条失败跳过 */ }
      }
      message.success(`刷新完成：成功 ${ok}/${keys.length} 条（数据实时来自科维斯）`);
    } finally {
      setLoading(false);
    }
  };

  // 科维斯订单 → 工单表单预填（新建工单逻辑与「调度待跟进工单」完全一致）
  const buildPrefill = (o) => {
    const send = o.send_info || {};
    const recv = o.receive_info || {};
    const services = [
      ...(send.service_items || []),
      ...(recv.service_items || []),
    ].filter(Boolean);
    const cargoLines = [];
    if (services.length) cargoLines.push(`服务项目: ${services.join('、')}`);
    if (recv.name) cargoLines.push(`联系人: ${recv.name} ${recv.tel || ''}`.trim());
    return {
      order_no: o.track_number || o.order_no || o.pro_number || '',
      business_type: o.send_type === 2 ? 'ftl' : 'ltl',
      carrier: o.carrier_name || '',
      pickup_location: fmtLocation(send),
      delivery_location: fmtLocation(recv),
      estimated_delivery: o.take_time_text || '',
    };
  };

  // 按查询编码精确查找系统内已生成的调度工单（跨客户权限，持单号即授权）
  const handleViewOrder = async (o) => {
    const kw = o.track_number || o.order_no || o.pro_number || '';
    if (!kw) { message.warning('缺少单号，无法查找工单'); return; }
    setFindingOrder(true);
    try {
      const d = await orderApi.lookupByNo(kw);
      if (!d?.found) {
        message.info('该科维斯订单尚未生成调度工单，可点「生成调度」创建');
        return;
      }
      if (d.found && d.authorized === false) {
        message.warning('该调度工单未分配给您当前账号，无权查看；请联系管理员在「客户添加」中分配');
        return;
      }
      setOrderDrawer(d);
    } catch (e) {
      // 拦截器已提示
    } finally {
      setFindingOrder(false);
    }
  };

  const columns = [
    {
      title: '平台订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 150,
      render: (v) => <Text strong copyable>{v || '-'}</Text>,
    },
    { title: '查询编码', dataIndex: 'track_number', key: 'track_number', width: 110, render: (v) => v || '-' },
    { title: 'PRO号', dataIndex: 'pro_number', key: 'pro_number', width: 120, render: (v) => v || '-' },
    { title: '客户订单号', dataIndex: 'custom_number', key: 'custom_number', width: 120, render: (v) => v || '-' },
    { title: '承运商', dataIndex: 'carrier_name', key: 'carrier_name', width: 140, ellipsis: true, render: (v) => v || '-' },
    { title: '类型', dataIndex: 'send_type_text', key: 'send_type_text', width: 70, render: (v) => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v, r) => <Tag color={STATUS_COLOR[v] || 'default'}>{r.status_text || v || '-'}</Tag>,
    },
    { title: '提货日期', dataIndex: 'take_time_text', key: 'take_time_text', width: 110, render: (v) => v || '-' },
    {
      title: '提货 → 送货',
      key: 'route',
      ellipsis: true,
      render: (_, r) => {
        const s = r.send_info || {};
        const g = r.receive_info || {};
        return `${s.city || ''},${s.state_name || ''} → ${g.city || ''},${g.state_name || ''}`;
      },
    },
    {
      title: '订单金额',
      key: 'charge',
      width: 110,
      render: (_, r) => {
        const amt = r.charge?.total_charge ?? r.charge?.amount ?? '';
        return amt !== '' ? `$${amt}` : '-';
      },
    },
    {
      title: '查询时间',
      dataIndex: '_queried_at',
      key: '_queried_at',
      width: 150,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 340,
      fixed: 'right',
      render: (_, record) => {
        const existInfo = existMap[orderKeyOf(record)];
        const statusText = existInfo?.status ? (ORDER_STATUS_MAP[existInfo.status]?.[0] || '') : '';
        const tip = existInfo?.authorized === false
          ? '该单已在调度系统生成工单，但未分配给您当前账号，无权查看'
          : `该单已在调度系统生成工单${statusText ? `（${statusText}）` : ''}，点击查看详情`;
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', columnGap: 8 }}>
            <Space size={4} wrap>
              <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(record)}>详情</Button>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={() => setPrefill(buildPrefill(record))}
              >
                生成调度
              </Button>
              <Button
                size="small"
                style={{ backgroundColor: '#000', color: '#fff', borderColor: '#000' }}
                onClick={() => {
                  setAppealTarget(record);
                  appealForm.setFieldsValue({
                    appeal_reason: 'other',
                    customer_name: '',
                    customer_owner: '',
                    follow_strategy: 'no',
                    escalate_hours: '',
                    appeal_detail: '',
                    appeal_amount: '',
                    channel_party: record.carrier_name || '',
                  });
                  setAppealModalOpen(true);
                }}
              >
                账单申诉
              </Button>
              <Button
                size="small"
                style={{ backgroundColor: '#cf1322', color: '#fff', borderColor: '#cf1322' }}
                onClick={() => {
                  setClaimTarget(record);
                  claimForm.setFieldsValue({
                    claim_type: 'damage',
                    claim_amount: '',
                    claim_reason: '',
                    claim_detail: '',
                    customer_name: '',
                    customer_owner: '',
                    contact_info: '',
                    follow_strategy: 'no',
                    escalate_hours: '',
                  });
                  setClaimModalOpen(true);
                }}
              >
                提交索赔
              </Button>
            </Space>
            {existInfo?.exists && (
              <Tooltip title={tip}>
                <Tag
                  color="green"
                  onClick={() => handleViewOrder(record)}
                  style={{ margin: '4px 0 0', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  已有工单
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const handleAppealPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    await uploadAppealFiles(files);
  };

  const APPEAL_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
  const APPEAL_IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20MB（与后端 upload-file 一致）

  const uploadAppealFiles = async (files) => {
    const ok = [], bad = [];
    for (const f of files) {
      const mime = f.type || '';
      const ext = (f.name || '').split('.').pop()?.toLowerCase() || '';
      const isImage = mime.startsWith('image/') || APPEAL_IMAGE_EXTS.includes(ext);
      if (!isImage || (!APPEAL_IMAGE_MIMES.includes(mime) && !APPEAL_IMAGE_EXTS.includes(ext))) {
        bad.push(`${f.name || '文件'}（仅支持 png/jpg/jpeg/gif/webp）`);
        continue;
      }
      if (f.size > MAX_UPLOAD_BYTES) {
        bad.push(`${f.name || '文件'}（超过 20MB）`);
        continue;
      }
      ok.push(f);
    }
    if (bad.length) {
      message.warning(`已跳过 ${bad.length} 个文件：${bad.slice(0, 3).join('；')}${bad.length > 3 ? ' 等' : ''}`);
    }
    if (!ok.length) return;
    setAppealUploading(true);
    try {
      const newAttachments = [];
      for (const f of ok) {
        const d = await appealApi.uploadFile(f);
        newAttachments.push({ url: d.url, name: d.name || f.name, size: f.size, mime: f.type });
      }
      setAppealImages((prev) => [...prev, ...newAttachments]);
      message.success(`已上传 ${newAttachments.length} 张图片`);
    } catch (err) {
      message.error(err?.response?.data?.detail || '文件上传失败');
    } finally {
      setAppealUploading(false);
    }
  };

  const handleAppealFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    uploadAppealFiles(files);
    // 清空 input，以便再次选相同文件也能触发 onChange
    e.target.value = '';
  };

  const removeAppealImage = (url) => {
    setAppealImages((prev) => prev.filter((a) => a.url !== url));
  };

  const handleClaimPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (!files.length) return;
    e.preventDefault();
    setClaimUploading(true);
    try {
      const newAttachments = [];
      for (const f of files) {
        const d = await claimApi.uploadFile(f);
        newAttachments.push({ url: d.url, name: d.name || f.name, isImage: f.type.startsWith('image/') });
      }
      setClaimImages((prev) => [...prev, ...newAttachments]);
      message.success(`已粘贴 ${files.length} 个文件`);
    } catch (err) {
      message.error(err?.response?.data?.detail || '文件上传失败');
    } finally {
      setClaimUploading(false);
    }
  };

  const removeClaimImage = (url) => {
    setClaimImages((prev) => prev.filter((a) => a.url !== url));
  };

  return (
    <div>
      <div className="page-header">
        <h2>科维斯订单</h2>
        <p className="desc">
          实时查询科维斯平台订单（数据不存入本系统，刷新页面后需重新查询）：输入查询编码 / PRO号 / BOL号 / 订单编号精确查询，点击「生成调度」可直接带入信息新建工单
        </p>
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="查询编码 / PRO号 / BOL号 / 订单编号"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 360 }}
            allowClear
          />
          <Button type="primary" loading={loading} onClick={handleSearch}>查询订单</Button>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={handleRefresh}>
            刷新{orders.length ? `（${orders.length}条）` : ''}
          </Button>
          {orders.length > 0 && (
            <Button icon={<ClearOutlined />} onClick={() => { setOrders([]); setExistMap({}); localStorage.removeItem(CACHE_KEY()); }}>清空历史</Button>
          )}
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey={(r) => r.order_no || r.track_number || Math.random()}
          loading={loading}
          scroll={{ x: 1480 }}
          locale={{ emptyText: '暂无数据：输入单号点「查询订单」实时获取科维斯订单' }}
          pagination={false}
        />
      </Card>

      {/* 科维斯订单详情 */}
      <Drawer
        title="科维斯订单详情"
        open={!!detail}
        onClose={() => setDetail(null)}
        width={620}
        extra={
          detail && (
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setPrefill(buildPrefill(detail))}>
              生成调度
            </Button>
          )
        }
      >
        {detail && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="平台订单号" span={2}>{detail.order_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="查询编码">{detail.track_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="PRO号">{detail.pro_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="客户订单号">{detail.custom_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="退货单号">{detail.returns_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="承运商">{detail.carrier_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="发货类型">{detail.send_type_text || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Space size={8}>
                  <Tag color={STATUS_COLOR[detail.status] || 'default'} style={{ margin: 0 }}>{detail.status_text || '-'}</Tag>
                  <Button
                    type="link"
                    size="small"
                    icon={<FileSearchOutlined />}
                    loading={findingOrder}
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => handleViewOrder(detail)}
                  >
                    查看详情
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="提货日期">{detail.take_time_text || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{detail.create_time_text || '-'}</Descriptions.Item>
            </Descriptions>

            <Card size="small" type="inner" title="发货信息" style={{ marginBottom: 12 }}>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{fmtLocation(detail.send_info)}</Paragraph>
              {detail.send_info?.name && (
                <div style={{ marginTop: 8 }}>
                  联系人：{detail.send_info.name} {detail.send_info.tel} {detail.send_info.email}
                </div>
              )}
              {(detail.send_info?.service_items || []).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {detail.send_info.service_items.map((s) => <Tag key={s} color="blue">{s}</Tag>)}
                </div>
              )}
            </Card>

            <Card size="small" type="inner" title="收货信息" style={{ marginBottom: 12 }}>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{fmtLocation(detail.receive_info)}</Paragraph>
              {detail.receive_info?.name && (
                <div style={{ marginTop: 8 }}>
                  联系人：{detail.receive_info.name} {detail.receive_info.tel} {detail.receive_info.email}
                </div>
              )}
              {(detail.receive_info?.service_items || []).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {detail.receive_info.service_items.map((s) => <Tag key={s} color="blue">{s}</Tag>)}
                </div>
              )}
            </Card>

            {detail.charge && (
              <Card size="small" type="inner" title="客户价格">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="运费">${detail.charge.line_charge ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="燃油费">${detail.charge.fuel_charge ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="附加费">${detail.charge.accessorial_charge ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="保险费">${detail.charge.insured_charge ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="订单金额"><Text strong>${detail.charge.total_charge ?? '-'}</Text></Descriptions.Item>
                  <Descriptions.Item label="补贴金额">${detail.charge.subsidy_price ?? '-'}</Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </>
        )}
      </Drawer>

      {/* 调度工单只读详情（按单号精确查到的系统工单） */}
      <Drawer
        title="调度工单详情"
        open={!!orderDrawer}
        onClose={() => setOrderDrawer(null)}
        width={640}
        className="order-detail-drawer"
      >
        {orderDrawer?.order && (() => {
          const o = orderDrawer.order;
          const [sText, sColor] = ORDER_STATUS_MAP[o.status] || ['调度待跟进', 'default'];
          const [pText, pColor] = PRIORITY_MAP[o.priority] || [o.priority || '-', 'default'];
          const logs = orderDrawer.logs || [];
          return (
            <>
              <Space style={{ marginBottom: 12 }}>
                <Tag color={sColor}>{sText}</Tag>
                <Tag color={pColor}>{pText}</Tag>
                <Text strong copyable>{o.order_no}</Text>
              </Space>
              <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="客户名称" span={2}>{o.customer_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="承运商">{o.carrier || '-'}</Descriptions.Item>
                <Descriptions.Item label="类型">{o.business_type === 'ftl' ? 'FTL' : 'LTL'}</Descriptions.Item>
                <Descriptions.Item label="提货地址" span={2}>{o.pickup_location || '-'}</Descriptions.Item>
                <Descriptions.Item label="送货地址" span={2}>{o.delivery_location || '-'}</Descriptions.Item>
                <Descriptions.Item label="收货人">{o.consignee || '-'}</Descriptions.Item>
                <Descriptions.Item label="预计送达">{o.estimated_delivery || '-'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{fmtCN(o.created_at) || '-'}</Descriptions.Item>
                <Descriptions.Item label="完成时间">{fmtCN(o.completed_at) || '-'}</Descriptions.Item>
              </Descriptions>

              <Card size="small" type="inner" title="核心问题/异常情况" style={{ marginBottom: 12 }}>
                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{renderLatestSegment(o.core_issue)}</Paragraph>
                {(o.issue_images || []).length > 0 && (
                  <Space wrap style={{ marginTop: 8 }}>
                    {o.issue_images.map((u) => (
                      <Image key={u} src={u} width={120} height={90} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ))}
                  </Space>
                )}
              </Card>

              {o.handling_strategy && (
                <Card size="small" type="inner" title="处理思路" style={{ marginBottom: 12 }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{o.handling_strategy}</Paragraph>
                </Card>
              )}
              {o.follow_up_plan && (
                <Card size="small" type="inner" title="跟进策略" style={{ marginBottom: 12 }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{o.follow_up_plan}</Paragraph>
                </Card>
              )}
              {o.actual_action && (
                <Card size="small" type="inner" title="实际操作/结果反馈" style={{ marginBottom: 12 }}>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{[o.actual_action, o.result_feedback].filter(Boolean).join('\n')}</Paragraph>
                </Card>
              )}

              <Divider orientation="left" style={{ margin: '8px 0 12px' }}>操作日志（最新在前）</Divider>
              {logs.length === 0 ? <Empty description="暂无日志" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                <Timeline
                  items={logs.map((l) => ({
                    children: (
                      <div>
                        <Space size={8} wrap>
                          <Text strong>{actionText(l.action)}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{fmtCN(l.created_at)}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{l.operator_name || '系统'}</Text>
                        </Space>
                        {l.remark && <div style={{ color: '#595959', marginTop: 2, whiteSpace: 'pre-wrap' }}>{parseContent(cleanLogRemark(l.remark))}</div>}
                        {(l.new_value && l.action !== 'status_change') && (
                          <div style={{ color: '#595959', marginTop: 2, whiteSpace: 'pre-wrap' }}>{parseContent(logDeltaText(l))}</div>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}
            </>
          );
        })()}
      </Drawer>

      {/* 生成调度 = 新建工单（与调度待跟进工单同一表单同一逻辑，字段已用科维斯订单预填） */}
      <CreateOrderModal
        open={!!prefill}
        initialValues={prefill}
        onClose={() => setPrefill(null)}
        onCreated={() => {
          message.success('已生成调度工单，可在「调度待跟进工单」查看');
          setPrefill(null);
          // 新建成功后立即重新检查，列表对应行显示「已有工单」
          mergeExistMap(orders);
        }}
      />

      {/* 账单申诉弹窗 */}
      <Modal
        title="账单申诉"
        open={appealModalOpen}
        width={720}
        onCancel={() => { setAppealModalOpen(false); setAppealImages([]); }}
        confirmLoading={appealSubmitting}
        onOk={async () => {
          try {
            const values = await appealForm.validateFields();
            setAppealSubmitting(true);
            const target = appealTarget || {};
            // attachments 以结构化方式随建单提交；详情也附带 Markdown 便于历史/AI 读取
            const attachMd = appealImages.map((a) =>
              `![${a.name}](${a.url})`
            ).join('\n');
            const detail = [values.appeal_detail || '', attachMd].filter(Boolean).join('\n');
            await appealApi.create({
              kewell_order_no: target.order_no || '',
              kewell_track: target.track_number || target.pro_number || '',
              original_amount: values.appeal_amount || '',
              channel: target.carrier_name || '',
              ...values,
              appeal_detail: detail,
              attachments: appealImages.map((a) => ({
                name: a.name, url: a.url, size: a.size || 0, mime: a.mime || '',
              })),
            });
            message.success('申诉工单已创建，可在「申诉管理 → 申诉待跟进工单」查看');
            setAppealModalOpen(false);
            appealForm.resetFields();
            setAppealImages([]);
            setAppealTarget(null);
          } catch (e) {
            if (e?.errorFields) return;
            message.error(e.message || '创建失败');
          } finally {
            setAppealSubmitting(false);
          }
        }}
        okText="提交申诉"
        cancelText="取消"
      >
        {appealTarget && (
          <div style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            来源订单：<Text copyable strong>{appealTarget.order_no || '-'}</Text>
            {' · '}查询编码: {appealTarget.track_number || '-'}
            {' · '}承运商: {appealTarget.carrier_name || '-'}
            {' · '}金额: ${appealTarget.charge?.total_charge ?? appealTarget.charge?.amount ?? '-'}
          </div>
        )}
        <Form form={appealForm} layout="vertical" requiredMark={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="appeal_reason" label="申诉原因" rules={[{ required: true, message: '请选择申诉原因' }]}>
                <Select options={APPEAL_REASON_OPTIONS} placeholder="请选择" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="appeal_amount" label="申诉金额">
                <Input placeholder="如：$150 / 38lb" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="appeal_detail"
            label="申诉资料（可粘贴截图/图片，也可直接写文字）"
            extra={
              <span style={{ color: '#8c8c8c' }}>
                Ctrl/⌘+V 粘贴图片 · 也可点右侧按钮选择图片上传（png/jpg/jpeg/gif/webp，单张≤20MB）
                {appealUploading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
                <label style={{ marginInlineStart: 12, cursor: 'pointer', color: '#1890ff' }}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleAppealFileInput}
                  />
                  选择图片上传
                </label>
              </span>
            }
          >
            <Input.TextArea
              rows={6}
              onPaste={handleAppealPaste}
              placeholder="粘贴或编辑申诉详细描述，支持多行。后续在申诉工单中所有新增内容都会形成历史对话记录。"
            />
          </Form.Item>
          {appealImages.length > 0 && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <Image.PreviewGroup>
                <Space wrap size={8}>
                  {appealImages.map((a) => (
                    <div key={a.url} style={{ position: 'relative', width: 72, height: 72 }}>
                      <Image src={a.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        title="删除"
                        onClick={() => removeAppealImage(a.url)}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                      />
                    </div>
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          )}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户" rules={[{ required: true, message: '请选择客户' }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="选择客户"
                  optionFilterProp="label"
                  options={customerList.map((c) => ({ value: c.name, label: c.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_owner" label="客户归属" rules={[{ required: true, message: '请选择客户归属' }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="选择客服账号"
                  optionFilterProp="label"
                  options={csUsers.map((u) => ({ value: u.full_name, label: u.full_name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="expected_result" label="客户预期可接受结果">
            <Input.TextArea rows={2} placeholder="如：退还尺寸差异 $50；或整体打 85 折" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="channel_party" label="渠道方">
                <Input placeholder="如：Estes Express 客服 / 科维斯平台" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="follow_strategy" label="是否遵循系统优先级规则和策略">
                <Select options={FOLLOW_STRATEGY_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="escalate_hours" label="多久未处理升级紧急">
            <Select allowClear placeholder="选择升级周期" options={ESCALATE_HOURS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 提交索赔弹窗 */}
      <Modal
        title="提交索赔"
        open={claimModalOpen}
        width={720}
        onCancel={() => { setClaimModalOpen(false); setClaimImages([]); }}
        confirmLoading={claimSubmitting}
        onOk={async () => {
          try {
            const values = await claimForm.validateFields();
            setClaimSubmitting(true);
            const target = claimTarget || {};
            const attachMd = claimImages.map((a) =>
              a.isImage ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`
            ).join('\n');
            const detail = [values.claim_detail || '', attachMd].filter(Boolean).join('\n');
            await claimApi.create({
              kewell_order_no: target.order_no || '',
              kewell_track: target.track_number || target.pro_number || '',
              original_amount: target.charge?.total_charge != null ? `$${target.charge.total_charge}` : (target.charge?.amount != null ? `$${target.charge.amount}` : ''),
              channel: target.carrier_name || '',
              ...values,
              claim_detail: detail,
            });
            message.success('索赔工单已创建，可在「索赔管理 → 索赔待跟进工单」查看');
            setClaimModalOpen(false);
            claimForm.resetFields();
            setClaimImages([]);
            setClaimTarget(null);
          } catch (e) {
            if (e?.errorFields) return;
            message.error(e.message || '创建失败');
          } finally {
            setClaimSubmitting(false);
          }
        }}
        okText="提交索赔"
        cancelText="取消"
      >
        {claimTarget && (
          <div style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            来源订单：<Text copyable strong>{claimTarget.order_no || '-'}</Text>
            {' · '}查询编码: {claimTarget.track_number || '-'}
            {' · '}承运商: {claimTarget.carrier_name || '-'}
            {' · '}金额: ${claimTarget.charge?.total_charge ?? claimTarget.charge?.amount ?? '-'}
          </div>
        )}
        <Form form={claimForm} layout="vertical" requiredMark={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="claim_type" label="索赔类型" rules={[{ required: true, message: '请选择索赔类型' }]}>
                <Select options={CLAIM_TYPE_OPTIONS} placeholder="请选择" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="claim_amount" label="索赔金额" rules={[{ required: true, message: '请填写索赔金额' }]}>
                <Input placeholder="如：$500" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="claim_reason" label="索赔原因 / 损失描述" rules={[{ required: true, message: '请填写索赔原因' }]}>
            <Input.TextArea rows={2} placeholder="如：收货时发现 2 托货物外箱破损，内物受潮；POD 已注明破损" />
          </Form.Item>
          <Form.Item
            name="claim_detail"
            label="索赔资料（BOL提货单 / POD签收单 / 商业发票 / 破损照片 / 检验报告等，可粘贴截图/文件）"
            extra={
              <span style={{ color: '#8c8c8c' }}>
                直接 Ctrl/⌘+V 粘贴图片或文件{claimUploading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
              </span>
            }
          >
            <Input.TextArea
              rows={6}
              onPaste={handleClaimPaste}
              placeholder="粘贴或编辑索赔资料说明，支持多行。美国LTL/FTL索赔通常需：BOL、POD、商业发票、破损照片、维修/替换发票等。"
            />
          </Form.Item>
          {claimImages.length > 0 && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <Image.PreviewGroup>
                <Space wrap size={8}>
                  {claimImages.map((a) => (
                    <div key={a.url} style={{ position: 'relative', width: 72, height: 72 }}>
                      {a.isImage ? (
                        <Image src={a.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
                      ) : (
                        <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#1890ff', textDecoration: 'none', wordBreak: 'break-all', padding: 4, textAlign: 'center' }}>
                          {a.name}
                        </a>
                      )}
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        title="删除"
                        onClick={() => removeClaimImage(a.url)}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                      />
                    </div>
                  ))}
                </Space>
              </Image.PreviewGroup>
            </div>
          )}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户">
                <Select
                  showSearch
                  allowClear
                  placeholder="选择客户"
                  optionFilterProp="label"
                  options={customerList.map((c) => ({ value: c.name, label: c.name }))}
                  onChange={(name) => {
                    const c = customerList.find((x) => x.name === name);
                    claimForm.setFieldsValue({ customer_owner: matchOwnerName(c) });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_owner" label="客户归属">
                <Select
                  showSearch
                  allowClear
                  placeholder="选择客服账号"
                  optionFilterProp="label"
                  options={assignableUsers.map((u) => ({ value: u.full_name, label: u.full_name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contact_info" label="联系信息">
                <Input placeholder="邮箱 / 电话（承运商回函用）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="follow_strategy" label="是否遵循系统优先级规则和策略">
                <Select options={FOLLOW_STRATEGY_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="escalate_hours" label="多久未处理升级紧急">
            <Select allowClear placeholder="选择升级周期" options={ESCALATE_HOURS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
